import type {ReactElement} from 'react';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {isFacebookAccount, isInstagramAccount, isTikTokAccount, isTwitterAccount, isYoutubeAccount} from '@Components/social-media/account.vo';
import {SocialProfileIcon, SocialProfileIconSize} from '@Components/social-media/components/social-profile-icon';
import {Icon} from '@Components/icon-v2';
import {FlatIconColor, FlatIconColorType, IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import Divider from '@Components/divider';
import {getAssetUrl} from '@Utils/s3.util';
import {SocialThumbnail} from '@Components/social-media/components/social-thumbnail';
import {Text, TextSize} from '@Components/text';
import styles from './social-post-content-reel-top-panel.module.scss';

export interface SocialPostContentReelTopPanelProps {
  account: SocialAccount;
}

export function SocialPostContentReelTopPanel({account}: SocialPostContentReelTopPanelProps): ReactElement<any> {
  const youtubeDividerDimensions = {
    width: 2,
    height: 22,
  };

  const getIconName = (): string => {
    if (isYoutubeAccount(account)) {
      return 'icon-yt-shorts';
    }

    return 'icon-insta-reels';
  };

  const getReelTopPanelStrip = (): ReactElement<any> => {
    if (isYoutubeAccount(account)) {
      return (
        <div className={styles.youtubeTopPanel}>
          <Icon
            icon={getIconName()}
            shape={IconShape.NONE}
            type={IconType.FLAT}
            flatIconType={{
              color: FlatIconColor.NONE,
              type: FlatIconColorType.DEFAULT,
            }}
            size={IconSize.SIZE_ICON_24}
            className={styles.youtubeColor}
          />
          <Divider width={youtubeDividerDimensions.width} height={youtubeDividerDimensions.height} additionalClasses="bg-neutral-11 spacing-m-r-3 spacing-m-l-2" />
          <img className={styles.youtubeLogoImage} src={getAssetUrl('youtube-official-logo.png')} alt="YouTube" />
        </div>
      );
    }

    return (
      <>
        {!isTikTokAccount(account) ? (
          <Icon
            icon={getIconName()}
            shape={IconShape.CIRCLE}
            type={IconType.FLAT}
            flatIconType={{
              color: FlatIconColor.NEUTRAL,
              type: FlatIconColorType.DEFAULT,
            }}
            size={IconSize.SIZE_ICON_20}
            className="spacing-m-r-1"
          />
        ) : null}
        {account !== undefined ? <SocialThumbnail isActive={false} type={account?.type} /> : null}
      </>
    );
  };

  const renderPostInfo = (): ReactElement<any> => {
    const isHorizontal = isTwitterAccount(account) || isInstagramAccount(account);
    const textClassName = 'content-sub-text';

    return (
      <div className={`${isHorizontal ? `${styles.postInfoHorizontal} _fit-width _full-height` : `${styles.postInfoVertical} _fit-width _full-height`}`}>
        <Text val={`${account?.name}`} className="content-body" size={TextSize.XSMALL} bold />
        {isFacebookAccount(account) ? (
          <Text val={`${window.i18next.t('pmwjs_time_placeholder')}`} className={textClassName} size={TextSize.XXSMALL} />
        ) : (
          <Text val={`. ${window.i18next.t('pmwjs_time_placeholder')}`} className={`${textClassName} spacing-m-l-1`} size={TextSize.XXSMALL} />
        )}
      </div>
    );
  };

  const renderPostInfoWrapper = (): ReactElement<any> => {
    if (isFacebookAccount(account)) {
      return (
        <div className="flex-row-align-center">
          <div className="spacing-m-r-2">{account !== undefined ? <SocialProfileIcon account={account} size={SocialProfileIconSize.LARGE} showPlatformIcon={false} /> : null}</div>
          {renderPostInfo()}
        </div>
      );
    }

    return <div />;
  };

  return (
    <div className={`${styles.topReel} _fit-height ${isFacebookAccount(account) ? styles.facebookTopReelGradient : ''} ${styles.reelCaptionContainer} _full-width _fit-height`}>
      {renderPostInfoWrapper()}
      <div className="flex-row-justify-center flex-items-center">{getReelTopPanelStrip()}</div>
    </div>
  );
}
